import { useState, useRef, useEffect } from 'react';
import { getWorkspaceMembers } from '../../API/services/companyService';
import { t } from '../../i18n/i18n';
import './MentionTextarea.scss';

const MentionTextarea = ({ value, onChange, placeholder, maxLength = 1000, className = '' }) => {
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (showMentionDropdown && mentionSearch) {
      fetchMembers(mentionSearch);
    }
  }, [mentionSearch, showMentionDropdown]);

  const fetchMembers = async (search) => {
    try {
      const { collection } = await getWorkspaceMembers({ search, limit: 10 });
      setFilteredMembers(collection || []);
    } catch (error) {
      console.error('Error fetching workspace members:', error);
      setFilteredMembers([]);
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    onChange(newValue);

    const textBeforeCursor = newValue.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      setMentionSearch(mentionMatch[1]);
      setShowMentionDropdown(true);
      setSelectedMentionIndex(0);

      const textarea = textareaRef.current;
      const coordinates = getCaretCoordinates(textarea, cursorPos);
      setMentionPosition({
        top: coordinates.top + 20,
        left: coordinates.left,
      });
    } else {
      setShowMentionDropdown(false);
    }
  };

  const getCaretCoordinates = (element, position) => {
    const div = document.createElement('div');
    const style = getComputedStyle(element);

    div.style.position = 'absolute';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';
    div.style.font = style.font;
    div.style.padding = style.padding;
    div.style.border = style.border;
    div.style.width = `${element.offsetWidth}px`;
    div.style.visibility = 'hidden';

    const text = element.value.substring(0, position);
    div.textContent = text;

    const span = document.createElement('span');
    span.textContent = element.value.substring(position) || '.';
    div.appendChild(span);

    document.body.appendChild(div);

    const coordinates = {
      top: span.offsetTop,
      left: span.offsetLeft,
    };

    document.body.removeChild(div);
    return coordinates;
  };

  const insertMention = (member) => {
    const email = member.email || member.partnerEmail;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);

    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      const beforeMention = textBeforeCursor.substring(0, mentionMatch.index);
      const newValue = `${beforeMention}@${email} ${textAfterCursor}`;
      onChange(newValue);

      setTimeout(() => {
        const newCursorPos = beforeMention.length + email.length + 2;
        textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current?.focus();
      }, 0);
    }

    setShowMentionDropdown(false);
    setMentionSearch('');
  };

  const handleKeyDown = (e) => {
    if (showMentionDropdown && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev < filteredMembers.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        insertMention(filteredMembers[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
      }
    }
  };

  const renderTextWithMentions = () => {
    if (!value) return '';

    const emailRegex = /@([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = emailRegex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push(value.substring(lastIndex, match.index));
      }
      parts.push(
        <strong key={match.index} className="mention-text">
          @{match[1]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < value.length) {
      parts.push(value.substring(lastIndex));
    }

    return parts;
  };

  return (
    <div className={`mention-textarea-container ${className}`} data-testid="mention-textarea">
      <textarea
        ref={textareaRef}
        className="mention-textarea-input"
        placeholder={placeholder}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        maxLength={maxLength}
        data-testid="mention-textarea-input"
      />

      {showMentionDropdown && filteredMembers.length > 0 && (
        <div
          ref={dropdownRef}
          className="mention-dropdown"
          style={{
            top: `${mentionPosition.top}px`,
            left: `${mentionPosition.left}px`,
          }}
          data-testid="mention-dropdown"
        >
          {filteredMembers.map((member, index) => {
            const email = member.email || member.partnerEmail;
            const name = member.firstName
              ? `${member.firstName} ${member.lastName}`
              : member.partnerName;

            return (
              <div
                key={email}
                className={`mention-option ${
                  index === selectedMentionIndex ? 'selected-text' : ''
                }`}
                onClick={() => insertMention(member)}
                data-testid={`mention-option-${index}`}
              >
                <div className="mention-option-name">{name}</div>
                <div className="mention-option-email">{email}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="character-counter text-xs text-muted">
        {value?.length || 0} / {maxLength}
      </div>
    </div>
  );
};

export default MentionTextarea;
